import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import {
    ErrorStatusTypes,
    ErrorsStack,
    parseErrorMessage,
} from "components/common/errorsDeclarations";
import * as ordiniLavoroApi from "../../../../api/produzione/ordiniLavoroApi";
import { toast } from "react-toastify";
import { FiltriOrdiniLavoro } from "components/userPreference/userPreferenceSlice";
import * as assegnazioniApi from "../../../../api/produzione/assegnazioniApi";
// Import types condivisi
import {
    OrdineLavoro,
    OrdiniLavoroList,
    STATI_ORDINE_LAVORO,
    StatoOrdineLavoro,
    getStatoOrdineLavoroColor,
    isOrdineLavoroInRitardo,
    createOrdineLavoroVuoto, Assegnazione,
} from "./ordiniLavoroTypes";


// ============================================
// STATO SLICE
// ============================================

export interface OrdiniLavoroState {
    ordiniLavoroList: OrdiniLavoroList;
    ordineLavoroCorrenteId?: number;  // Solo ID, mai oggetto completo
}

const initialState: OrdiniLavoroState = {
    ordiniLavoroList: {
        count: 0,
        page: 0,
        num_pages: 0,
        next: undefined,
        previous: undefined,
        results: [],
        errorsStack: { status: ErrorStatusTypes.OK },
    },
    ordineLavoroCorrenteId: undefined,
};

// ============================================
// ASYNC THUNKS
// ============================================

export const fetchOrdiniLavoro = createAsyncThunk(
    "ordiniLavoro/fetchOrdiniLavoro",
    async (parametri: FiltriOrdiniLavoro = {}) => {
        return await ordiniLavoroApi.fetchOrdiniLavoro(parametri);
    }
);

export const getOrdineLavoro = createAsyncThunk(
    "ordiniLavoro/getOrdineLavoro",
    async (parametri: { ordineLavoroId: number }) => {
        return await ordiniLavoroApi.getOrdineLavoro(parametri.ordineLavoroId);
    }
);

export const saveOrdineLavoro = createAsyncThunk(
    "ordiniLavoro/saveOrdineLavoro",
    async (parametri: { ordineLavoroToSave: OrdineLavoro; azione?: string }) => {
        return await ordiniLavoroApi.saveOrdineLavoro(
            parametri.ordineLavoroToSave,
            parametri.azione
        );
    }
);

export const deleteOrdineLavoro = createAsyncThunk(
    "ordiniLavoro/deleteOrdineLavoro",
    async (parametri: { ordineLavoroToDelete: OrdineLavoro }) => {
        return await ordiniLavoroApi.deleteOrdineLavoro(parametri.ordineLavoroToDelete);
    }
);

// Azioni speciali
export const confermaArrivoOrdineLavoro = createAsyncThunk(
    "ordiniLavoro/confermaArrivoOrdineLavoro",
    async (parametri: { ordineLavoro: OrdineLavoro; qta_arrivata?: number }) => {
        const ordineLavoroToSave = {
            ...parametri.ordineLavoro,
            qta_arrivata: parametri.qta_arrivata || parametri.ordineLavoro.qta_prevista,
        };
        return await ordiniLavoroApi.saveOrdineLavoro(ordineLavoroToSave, 'conferma_arrivo');
    }
);

export const completaOrdineLavoro = createAsyncThunk(
    "ordiniLavoro/completaOrdineLavoro",
    async (parametri: { ordineLavoro: OrdineLavoro }) => {
        return await ordiniLavoroApi.saveOrdineLavoro(parametri.ordineLavoro, 'completa');
    }
);


export const saveAssegnazione = createAsyncThunk(
    "ordiniLavoro/saveAssegnazione",
    async (parametri: { ordineLavoroId: number; assegnazione: Assegnazione; azione?:string }) => {
        await assegnazioniApi.saveAssegnazione(
            parametri.ordineLavoroId,
            parametri.assegnazione,
            parametri.azione
        );
        // Ritorna l'ordine di lavoro aggiornato per refresh
        return await ordiniLavoroApi.getOrdineLavoro(parametri.ordineLavoroId);
    }
);

export const deleteAssegnazione = createAsyncThunk(
    "ordiniLavoro/deleteAssegnazione",
    async (parametri: { ordineLavoroId: number; assegnazioneId: number }) => {
        await assegnazioniApi.deleteAssegnazione(
            parametri.ordineLavoroId,
            parametri.assegnazioneId
        );
        // Ritorna l'ordine di lavoro aggiornato per refresh
        return await ordiniLavoroApi.getOrdineLavoro(parametri.ordineLavoroId);
    }
);

// ============================================
// SLICE
// ============================================

export const ordiniLavoroSlice = createSlice({
    name: "ordiniLavoro",
    initialState,
    reducers: {
        setOrdineLavoroCorrenteId: (state, action: PayloadAction<number | undefined>) => {
            state.ordineLavoroCorrenteId = action.payload;
        },
        resetOrdiniLavoro: (state) => {
            state.ordineLavoroCorrenteId = undefined;
            state.ordiniLavoroList = initialState.ordiniLavoroList;
        },
    },
    extraReducers: (builder) => {
        // ============================================
        // FETCH (Lista)
        // ============================================
        builder.addCase(fetchOrdiniLavoro.pending, (state) => {
            state.ordiniLavoroList.errorsStack = { status: ErrorStatusTypes.PENDING };
        });
        builder.addCase(fetchOrdiniLavoro.fulfilled, (state, action) => {
            state.ordiniLavoroList = {
                ...action.payload,
                errorsStack: { status: ErrorStatusTypes.OK },
            };
        });
        builder.addCase(fetchOrdiniLavoro.rejected, (state, action) => {
            state.ordiniLavoroList.errorsStack = parseErrorMessage(action.error);
        });

        // ============================================
        // GET (Singolo)
        // ============================================
        builder.addCase(getOrdineLavoro.pending, (state) => {
            state.ordiniLavoroList.errorsStack = { status: ErrorStatusTypes.PENDING };
        });
        builder.addCase(getOrdineLavoro.fulfilled, (state, action) => {
            // Rimuove versione esistente
            state.ordiniLavoroList.results = state.ordiniLavoroList.results.filter(
                (item) => item.id !== action.payload.id
            );
            // Aggiunge versione aggiornata
            state.ordiniLavoroList.results.push(action.payload);
            // Ordina per id decrescente (più recenti prima)
            state.ordiniLavoroList.results.sort((a, b) => (b.id || 0) - (a.id || 0));

            state.ordineLavoroCorrenteId = action.payload.id;
            state.ordiniLavoroList.errorsStack = { status: ErrorStatusTypes.OK };
        });
        builder.addCase(getOrdineLavoro.rejected, (state, action) => {
            state.ordiniLavoroList.errorsStack = parseErrorMessage(action.error);
        });

        // ============================================
        // SAVE (Create/Update) - NO TOAST (usa AutoSaveIndicator)
        // ============================================
        builder.addCase(saveOrdineLavoro.pending, (state) => {
            state.ordiniLavoroList.errorsStack = { status: ErrorStatusTypes.PENDING };
        });
        builder.addCase(saveOrdineLavoro.fulfilled, (state, action) => {
            const index = state.ordiniLavoroList.results.findIndex(
                (item) => item.id === action.payload.id
            );

            if (index !== -1) {
                // Update esistente
                state.ordiniLavoroList.results[index] = {
                    ...action.payload,
                    errorsStack: { status: ErrorStatusTypes.SUCCESS },
                };
            } else {
                // Nuovo elemento
                state.ordiniLavoroList.results.unshift({
                    ...action.payload,
                    errorsStack: { status: ErrorStatusTypes.SUCCESS },
                });
                state.ordiniLavoroList.count++;
            }

            state.ordineLavoroCorrenteId = action.payload.id;
            state.ordiniLavoroList.errorsStack = { status: ErrorStatusTypes.SUCCESS };

            // NO toast.success() - usa AutoSaveIndicator nel form
        });
        builder.addCase(saveOrdineLavoro.rejected, (state, action) => {
            // Aggiorna errore sull'elemento specifico
            const ordineLavoroId = action.meta.arg.ordineLavoroToSave.id;
            if (ordineLavoroId) {
                state.ordiniLavoroList.results = state.ordiniLavoroList.results.map((item) => {
                    if (item.id === ordineLavoroId) {
                        return {
                            ...item,
                            errorsStack: parseErrorMessage(action.error),
                        };
                    }
                    return item;
                });
            }
            state.ordiniLavoroList.errorsStack = parseErrorMessage(action.error);
        });

        // ============================================
        // DELETE - OK Toast qui (no form)
        // ============================================
        builder.addCase(deleteOrdineLavoro.pending, (state) => {
            state.ordiniLavoroList.errorsStack = { status: ErrorStatusTypes.PENDING };
        });
        builder.addCase(deleteOrdineLavoro.fulfilled, (state, action) => {
            state.ordiniLavoroList.results = state.ordiniLavoroList.results.filter(
                (item) => item.id !== action.meta.arg.ordineLavoroToDelete.id
            );
            state.ordiniLavoroList.count--;
            state.ordiniLavoroList.errorsStack = { status: ErrorStatusTypes.OK };

            toast.success("Ordine di lavoro eliminato con successo");
        });
        builder.addCase(deleteOrdineLavoro.rejected, (state, action) => {
            state.ordiniLavoroList.errorsStack = parseErrorMessage(action.error);
            toast.error("Errore nell'eliminazione dell'ordine di lavoro");
        });

        // ============================================
        // AZIONI SPECIALI
        // ============================================

        // Conferma arrivo
        builder.addCase(confermaArrivoOrdineLavoro.pending, (state) => {
            state.ordiniLavoroList.errorsStack = { status: ErrorStatusTypes.PENDING };
        });
        builder.addCase(confermaArrivoOrdineLavoro.fulfilled, (state, action) => {
            const index = state.ordiniLavoroList.results.findIndex(
                (item) => item.id === action.payload.id
            );
            if (index !== -1) {
                state.ordiniLavoroList.results[index] = {
                    ...action.payload,
                    errorsStack: { status: ErrorStatusTypes.SUCCESS },
                };
            }
            state.ordiniLavoroList.errorsStack = { status: ErrorStatusTypes.SUCCESS };
            toast.success("Arrivo confermato");
        });
        builder.addCase(confermaArrivoOrdineLavoro.rejected, (state, action) => {
            state.ordiniLavoroList.errorsStack = parseErrorMessage(action.error);
            toast.error("Errore nella conferma arrivo");
        });

        // Completa ordine lavoro
        builder.addCase(completaOrdineLavoro.pending, (state) => {
            state.ordiniLavoroList.errorsStack = { status: ErrorStatusTypes.PENDING };
        });
        builder.addCase(completaOrdineLavoro.fulfilled, (state, action) => {
            const index = state.ordiniLavoroList.results.findIndex(
                (item) => item.id === action.payload.id
            );
            if (index !== -1) {
                state.ordiniLavoroList.results[index] = {
                    ...action.payload,
                    errorsStack: { status: ErrorStatusTypes.SUCCESS },
                };
            }
            state.ordiniLavoroList.errorsStack = { status: ErrorStatusTypes.SUCCESS };
            toast.success("Ordine di lavoro completato");
        });
        builder.addCase(completaOrdineLavoro.rejected, (state, action) => {
            state.ordiniLavoroList.errorsStack = parseErrorMessage(action.error);
            toast.error("Errore nel completamento");
        });


// Save Assegnazione
        builder.addCase(saveAssegnazione.pending, (state) => {
            state.ordiniLavoroList.errorsStack = { status: ErrorStatusTypes.PENDING };
        });
        builder.addCase(saveAssegnazione.fulfilled, (state, action) => {
            const index = state.ordiniLavoroList.results.findIndex(
                (item) => item.id === action.payload.id
            );
            if (index !== -1) {
                state.ordiniLavoroList.results[index] = {
                    ...action.payload,
                    errorsStack: { status: ErrorStatusTypes.SUCCESS },
                };
            }
            state.ordiniLavoroList.errorsStack = { status: ErrorStatusTypes.SUCCESS };
            toast.success("Assegnazione salvata");
        });
        builder.addCase(saveAssegnazione.rejected, (state, action) => {
            state.ordiniLavoroList.errorsStack = parseErrorMessage(action.error);
            toast.error("Errore nel salvataggio dell'assegnazione");
        });

// Delete Assegnazione
        builder.addCase(deleteAssegnazione.pending, (state) => {
            state.ordiniLavoroList.errorsStack = { status: ErrorStatusTypes.PENDING };
        });
        builder.addCase(deleteAssegnazione.fulfilled, (state, action) => {
            const index = state.ordiniLavoroList.results.findIndex(
                (item) => item.id === action.payload.id
            );
            if (index !== -1) {
                state.ordiniLavoroList.results[index] = {
                    ...action.payload,
                    errorsStack: { status: ErrorStatusTypes.SUCCESS },
                };
            }
            state.ordiniLavoroList.errorsStack = { status: ErrorStatusTypes.SUCCESS };
            toast.success("Assegnazione eliminata");
        });
        builder.addCase(deleteAssegnazione.rejected, (state, action) => {
            state.ordiniLavoroList.errorsStack = parseErrorMessage(action.error);
            toast.error("Errore nell'eliminazione dell'assegnazione");
        });
    },
});

// ============================================
// EXPORTS
// ============================================

export const {
    setOrdineLavoroCorrenteId,
    resetOrdiniLavoro,
} = ordiniLavoroSlice.actions;

export const ordiniLavoroSliceReducer = ordiniLavoroSlice.reducer;